import { supabase } from '@/integrations/supabase/client';

interface SendPartnerNotificationParams {
  babyId: string;
  type: 'food_log' | 'achievement' | 'streak_danger';
  title: string;
  body: string;
  metadata?: Record<string, any>;
}

/**
 * Send notification to all partners who have shared access to a baby
 * Only sends if the partner has enabled that specific notification type
 */
export async function sendPartnerNotification({
  babyId,
  type,
  title,
  body,
  metadata = {},
}: SendPartnerNotificationParams) {
  try {
    const currentUserId = (await supabase.auth.getUser()).data.user?.id;
    if (!currentUserId) return;

    // Get all users with shared access to this baby (excluding current user)
    const { data: sharedAccess, error: accessError } = await supabase
      .from('shared_access')
      .select('invited_user_id, owner_user_id')
      .eq('baby_id', babyId)
      .not('accepted_at', 'is', null);

    if (accessError) {
      console.error('Error fetching shared access:', accessError);
      return;
    }

    if (!sharedAccess || sharedAccess.length === 0) return;

    // Get unique partner user IDs (excluding current user)
    const partnerIds = new Set<string>();
    sharedAccess.forEach(access => {
      if (access.invited_user_id !== currentUserId) {
        partnerIds.add(access.invited_user_id);
      }
      if (access.owner_user_id !== currentUserId) {
        partnerIds.add(access.owner_user_id);
      }
    });

    // Check each partner's notification preferences and send if enabled
    for (const partnerId of partnerIds) {
      const { data: prefs } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', partnerId)
        .eq('baby_id', babyId)
        .maybeSingle();

      // Check if this notification type is enabled for this partner
      const isEnabled = prefs ? {
        'food_log': prefs.partner_food_logs,
        'achievement': prefs.partner_achievements,
        'streak_danger': prefs.partner_streak_danger,
      }[type] ?? false : false;

      if (!isEnabled) continue;

      // Send notification via edge function
      await supabase.functions.invoke('send-notification', {
        body: {
          userId: partnerId,
          babyId,
          title,
          body,
          type: `partner_${type}`,
          metadata: {
            ...metadata,
            from_user_id: currentUserId,
          },
        },
      });
    }
  } catch (error) {
    console.error('Error sending partner notification:', error);
  }
}

/**
 * Check if streak is in danger (no logs in last 24 hours) and notify partners
 */
export async function checkAndNotifyStreakDanger(babyId: string, babyName: string) {
  try {
    const { data: lastLog } = await supabase
      .from('food_logs')
      .select('consumed_at')
      .eq('baby_id', babyId)
      .order('consumed_at', { ascending: false })
      .limit(1)
      .single();

    if (!lastLog) return;

    const hoursSinceLastLog = (new Date().getTime() - new Date(lastLog.consumed_at).getTime()) / (1000 * 60 * 60);

    // If more than 20 hours since last log, streak is in danger
    if (hoursSinceLastLog >= 20) {
      await sendPartnerNotification({
        babyId,
        type: 'streak_danger',
        title: '⚠️ Streak i fare!',
        body: `${babyName}s streak er i fare for å brytes. Det er ${Math.floor(hoursSinceLastLog)} timer siden sist logg.`,
        metadata: {
          babyName,
          hoursSinceLastLog: Math.floor(hoursSinceLastLog),
        },
      });
    }
  } catch (error) {
    console.error('Error checking streak danger:', error);
  }
}
